import {
  MonthlyProjection, YearSummary, ForecastAccount, CashFlowItem, ForecastSettings, IncomeStatement,
} from '../types/personalForecast';

/**
 * Things worth telling someone about their own forecast, worked out by rule rather than by
 * judgement: same forecast in, same list out, and every one quotes the user's own figures back so
 * they can check it. Nothing here estimates tax or recommends a product - it points at what the
 * numbers already say.
 *
 * `check` means the model is probably wrong - a salary that never stops, a balance that moved with
 * nothing behind it. `opportunity` means the model is right and worth acting on.
 */
export type InsightLevel = 'check' | 'opportunity';

export interface Insight {
  id: string;
  level: InsightLevel;
  title: string;
  detail: string;
  /** Marked seen. Still computed, so it comes back if you ask to see what you have hidden. */
  dismissed?: boolean;
}

/** OAS recovery tax bands, 2024, indexed forward at the forecast's own inflation rate. */
const OAS_CLAWBACK_START_2024 = 90997;
const OAS_CLAWBACK_BASE_YEAR = 2024;
/** The RRIF minimum withdrawal factor in the year you turn 71. */
const RRIF_MINIMUM_AT_71 = 0.0528;
/** Neither CPP nor OAS earns any further credit for waiting beyond this age. */
export const LAST_USEFUL_DEFERRAL_AGE = 70;

const money = (n: number) => `$${Math.round(n).toLocaleString()}`;

const indexed = (amount: number, fromYear: number, toYear: number, inflation: number) =>
  amount * Math.pow(1 + inflation / 100, Math.max(0, toYear - fromYear));

export function buildInsights(
  projections: MonthlyProjection[],
  years: YearSummary[],
  accounts: ForecastAccount[],
  cashFlows: CashFlowItem[],
  settings: ForecastSettings,
  incomeStatement?: IncomeStatement,
): Insight[] {
  const out: Insight[] = [];
  if (!projections.length) return out;

  // Rules look at the forecast only. Recorded history is fact, not a modelling mistake: a balance
  // that stepped in 2015 with no flow behind it is simply what the sheet says, and a year where
  // more left the bank than arrived already happened. Flagging either is noise.
  const forecast = projections.filter(p => p.source === 'forecast');
  const forecastYears = years.filter(y => y.source === 'forecast');
  if (!forecast.length) return out;
  const last = projections[projections.length - 1];
  const lastYear = Number(last.month.slice(0, 4));
  const birthYear = settings.birthYear || 0;
  const ageIn = (year: number) => (birthYear ? year - birthYear : null);
  const accountById = new Map(accounts.map(a => [a.id, a]));

  // --- Income that never stops -------------------------------------------------------------
  // A salary with no end date keeps paying to the last month of the plan. Thirty years out that
  // quietly assumes you never retire, and every retirement-age figure is then wrong.
  const endAge = ageIn(lastYear);
  if (endAge !== null && endAge >= 65) {
    const working = cashFlows.filter(cf =>
      cf.isInflow && !cf.toAccountId && !cf.endDate
      && ['Salary', 'Bonus'].includes(cf.category)
      && (last.flows[cf.id] || 0) > 0);
    if (working.length) {
      out.push({
        id: 'income-never-ends',
        level: 'check',
        title: `Still earning at ${endAge}`,
        detail: `${working.map(cf => cf.name || cf.category).join(', ')} ${working.length === 1 ? 'has' : 'have'} no end date, so ${working.length === 1 ? 'it keeps' : 'they keep'} paying through ${lastYear}, when you turn ${endAge}. If you plan to stop, give each one an end date - otherwise every figure after that age assumes you never did.`,
      });
    }
  }

  // A payment still aimed at a debt that is already gone used to be flagged here. It no longer is:
  // payments are trimmed to what is outstanding, so the cash stops when the debt does and the money
  // simply stays where it is. Nothing is lost and nothing needs fixing, which makes it noise - and
  // an advisory list that cries wolf is worse than no list at all.

  // --- Interest that never falls as the debt does ------------------------------------------
  for (const cf of cashFlows) {
    if (cf.isInflow || cf.toAccountId || cf.percentOfBalance || cf.endDate) continue;
    if (cf.category !== 'Debt Payments' || !(cf.amount > 0)) continue;
    out.push({
      id: `flat-interest-${cf.id}`,
      level: 'check',
      title: `${cf.name || cf.category} is a fixed amount`,
      detail: `It is ${money(cf.amount)} a month for the whole forecast, with no end date. Real interest falls as the balance does. Setting it as a rate on the account instead means it declines on its own and stops when the debt does.`,
    });
  }

  // --- Cash sitting idle --------------------------------------------------------------------
  const finalYearMonths = forecast.filter(p => p.month.startsWith(String(lastYear)));
  const spendPerMonth = finalYearMonths.length
    ? finalYearMonths.reduce((s, p) => s + p.totalOutflows, 0) / finalYearMonths.length : 0;
  for (const a of accounts) {
    if (a.bucket !== 'current' || a.sweepRule || a.side !== 'asset') continue;
    const bal = last.accounts[a.id]?.closingBalance ?? 0;
    if (spendPerMonth > 0 && bal > spendPerMonth * 12) {
      out.push({
        id: `idle-cash-${a.id}`,
        level: 'opportunity',
        title: `${a.name} holds ${money(bal)} by ${lastYear}`,
        detail: `That is about ${Math.round(bal / spendPerMonth)} months of spending sitting in a current account. A sweep rule that keeps a few months here and moves the rest would put the surplus to work.`,
      });
    }
  }

  // --- Balances that moved with nothing behind them ----------------------------------------
  if (incomeStatement) {
    const forecastOnly = new Set(forecastYears.map(y => y.year));
    const worst = incomeStatement.periods
      .filter(p => forecastOnly.has(p.slice(0, 4)))
      .map(p => ({ p, v: incomeStatement.otherReconciling[p] || 0 }))
      .sort((a, b) => Math.abs(b.v) - Math.abs(a.v))[0];
    const netWorthThen = years.find(y => worst && worst.p.startsWith(y.year))?.netWorth ?? last.netWorth;
    if (worst && Math.abs(worst.v) > Math.abs(netWorthThen) * 0.02) {
      out.push({
        id: 'unexplained-balance',
        level: 'check',
        title: `${money(Math.abs(worst.v))} unexplained in ${worst.p}`,
        detail: `A recorded balance moved that period with no flow, rate or revaluation behind it. It still reconciles, because the figure is carried as "Other - Reconciling" - but it is the one line on the income statement that is not really income.`,
      });
    }
  }

  // --- A year where you spend more than you earn --------------------------------------------
  const shortfall = forecastYears.find(y => y.netCashFlow < 0);
  if (shortfall) {
    const age = ageIn(Number(shortfall.year));
    out.push({
      id: 'negative-cash-year',
      level: 'check',
      title: `${shortfall.year} runs ${money(Math.abs(shortfall.netCashFlow))} short`,
      detail: `More leaves your current accounts than arrives${age !== null ? `, the year you turn ${age}` : ''}. That is fine if you mean to draw on savings - but nothing in the forecast does that automatically, so the shortfall comes straight off your bank balance.`,
    });
  }

  // --- What the RRIF forces out at 71, against the OAS clawback -----------------------------
  if (birthYear) {
    const year71 = birthYear + 71;
    const registered = accounts.filter(a =>
      a.side === 'asset' && a.bucket === 'retirement' && (a.afterTaxPercent ?? 100) < 100);
    if (registered.length) {
      const atEnd = registered.reduce((s, a) => s + (last.accounts[a.id]?.closingBalance ?? 0), 0);
      if (atEnd > 0) {
        const forced = atEnd * RRIF_MINIMUM_AT_71;
        const floor = indexed(OAS_CLAWBACK_START_2024, OAS_CLAWBACK_BASE_YEAR, year71, settings.inflationRate);
        if (forced > floor) {
          // A flag and a handoff, not advice. This forecast tracks a budget and models no
          // drawdown at all, so it is the wrong tool to answer what to do about this. The
          // Retirement Planner is the right one, and these are the numbers to take to it.
          out.push({
            id: 'rrif-clawback',
            level: 'opportunity',
            title: `Registered balances reach ${money(atEnd)} by ${lastYear}`,
            detail: `The year you turn 71 a minimum of ${(RRIF_MINIMUM_AT_71 * 100).toFixed(2)}% of that has to come out as taxable income - about ${money(forced)} - against an OAS recovery threshold of roughly ${money(floor)} in ${year71}. This forecast models no withdrawals at all, so it cannot tell you what to do about it. Worth testing these balances in the Retirement Planner.`,
          });
        }
      }
    }
  }

  const dismissed = new Set(settings.dismissedInsights || []);
  return out.map(i => ({ ...i, dismissed: dismissed.has(i.id) }));
}
